/**
 * ==========================================================
 * 🚨 MÓDULO CRÍTICO: INVENTARIO
 * ==========================================================
 * 
 * Este módulo maneja control de stock y movimientos.
 * 
 * ⚠️ NO MODIFICAR sin validar en preview primero.
 * 
 * Última actualización: 2025-12-08
 * ==========================================================
 */

import { useEffect, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ErrorBoundaryModule } from "@/components/ErrorBoundaryModule";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";
import { LoteCardMobile } from "@/components/inventario/LoteCardMobile";
import { MovimientoCardMobile } from "@/components/inventario/MovimientoCardMobile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, ArrowUp, ArrowDown, Minus, Package, List, AlertTriangle, Warehouse, Calendar, Boxes, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getCaducidadBadge, getStockStatusBadge } from "@/components/inventario/shared/badges";
import { NotificacionesSistema } from "@/components/NotificacionesSistema";
import { InventarioPorCategoria } from "@/components/inventario/InventarioPorCategoria";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RecepcionDetalleDialog } from "@/components/compras/RecepcionDetalleDialog";

const InventarioContent = () => {
  const isMobile = useIsMobile();
  const [lotes, setLotes] = useState<any[]>([]);
  const [movimientos, setMovimientos] = useState<any[]>([]);
  const [productos, setProductos] = useState<any[]>([]);
  const [bodegas, setBodegas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [lotesProximosVencer, setLotesProximosVencer] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingMovimiento, setEditingMovimiento] = useState<any | null>(null);
  const [filterTipo, setFilterTipo] = useState<string>("todos");
  const [filterFechaInicio, setFilterFechaInicio] = useState("");
  const [filterFechaFin, setFilterFechaFin] = useState("");
  const [filterBodega, setFilterBodega] = useState<string>("todas");
  const [recepcionDialogOpen, setRecepcionDialogOpen] = useState(false);
  const [selectedEntregaId, setSelectedEntregaId] = useState<string | null>(null);
  const [entregasPorOrden, setEntregasPorOrden] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    producto_id: "",
    tipo_movimiento: "entrada",
    cantidad: "",
    fecha_caducidad: "",
    fecha_ultima_fumigacion: "",
    lote: "",
    referencia: "",
    notas: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  // Suscripción Realtime para actualización automática cuando almacenista carga productos
  useEffect(() => {
    const channel = supabase
      .channel('inventario-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'inventario_lotes' },
        (payload) => {
          console.log('📦 Cambio en inventario_lotes:', payload);
          loadData();
          if (payload.eventType === 'UPDATE') {
            toast({
              title: "Stock actualizado",
              description: "Se ha actualizado el inventario",
            });
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'inventario_movimientos' },
        (payload) => {
          console.log('📝 Nuevo movimiento de inventario:', payload);
          loadData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadData = async () => {
    try {
      const [lotesData, movimientosData, productosData, bodegasData, entregasData] = await Promise.all([
        // Cargar lotes (entradas desde recepciones de compras)
        supabase
          .from("inventario_lotes")
          .select(`
            *,
            productos (nombre, codigo, unidad),
            bodegas (nombre),
            ordenes_compra (folio),
            recibido_por:profiles!recibido_por(full_name)
          `)
          .order("fecha_entrada", { ascending: false })
          .limit(200),
        // Cargar movimientos manuales
        supabase
          .from("inventario_movimientos")
          .select(`
            *,
            productos (nombre, codigo),
            profiles:usuario_id (full_name)
          `)
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("productos")
          .select("id, codigo, nombre, stock_actual, stock_minimo, unidad, categoria, maneja_caducidad, requiere_fumigacion, fecha_ultima_fumigacion")
          .eq("activo", true)
          .order("nombre"),
        supabase
          .from("bodegas")
          .select("id, nombre")
          .eq("activo", true)
          .order("nombre"),
        // Cargar entregas recibidas para poder abrir el detalle de recepción
        supabase
          .from("ordenes_compra_entregas")
          .select("id, orden_compra_id")
          .eq("status", "recibida"),
      ]);

      if (lotesData.error) throw lotesData.error;
      if (movimientosData.error) throw movimientosData.error;
      if (productosData.error) throw productosData.error;
      if (bodegasData.error) throw bodegasData.error;

      setLotes(lotesData.data || []);
      setMovimientos(movimientosData.data || []);
      setProductos(productosData.data || []);
      setBodegas(bodegasData.data || []);

      // Crear mapa orden_compra_id -> entrega_id para poder abrir recepción
      if (entregasData.data) {
        const mapa: Record<string, string> = {};
        for (const entrega of entregasData.data) {
          // Si hay múltiples entregas por orden, tomamos la primera
          if (!mapa[entrega.orden_compra_id]) {
            mapa[entrega.orden_compra_id] = entrega.id;
          }
        }
        setEntregasPorOrden(mapa);
      }

      // Construir resumen de productos con stock desde lotes
      const lotesActivos = (lotesData.data || []).filter((l: any) => l.cantidad_disponible > 0);
      const productoMap = new Map<string, { codigo: string; nombre: string; categoria: string; unidad: string; stockTotal: number; stockMinimo: number; tieneVencimiento: boolean }>();

      for (const prod of (productosData.data || [])) {
        productoMap.set(prod.id, {
          codigo: prod.codigo || "",
          nombre: prod.nombre,
          categoria: (prod as any).categoria || "Sin categoría",
          unidad: (prod as any).unidad || "pzas",
          stockTotal: prod.stock_actual || 0,
          stockMinimo: (prod as any).stock_minimo || 0,
          tieneVencimiento: false,
        });
      }

      // Detectar lotes próximos a vencer por producto
      const hoy = new Date();
      for (const lote of lotesActivos) {
        if (lote.fecha_caducidad) {
          const diasRestantes = Math.ceil((new Date(lote.fecha_caducidad).getTime() - hoy.getTime()) / 86400000);
          if (diasRestantes <= 30 && productoMap.has(lote.producto_id)) {
            productoMap.get(lote.producto_id)!.tieneVencimiento = true;
          }
        }
      }

      setProductosResumen(
        Array.from(productoMap.entries()).map(([id, p]) => ({ id, ...p }))
      );
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar fecha de caducidad si el producto la requiere y es una entrada
    if (formData.tipo_movimiento === "entrada" && selectedProduct?.maneja_caducidad && !formData.fecha_caducidad) {
      toast({
        title: "Error",
        description: "Este producto requiere fecha de caducidad para registrar entradas",
        variant: "destructive",
      });
      return;
    }

    // Validar fecha de fumigación si el producto la requiere y es una entrada
    if (formData.tipo_movimiento === "entrada" && selectedProduct?.requiere_fumigacion && !formData.fecha_ultima_fumigacion) {
      toast({
        title: "Error",
        description: "Este producto requiere fecha de última fumigación",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error("No hay sesión activa");

      const movimientoData = {
        producto_id: formData.producto_id,
        tipo_movimiento: formData.tipo_movimiento,
        cantidad: parseInt(formData.cantidad),
        fecha_caducidad: formData.fecha_caducidad || null,
        lote: formData.lote || null,
        referencia: formData.referencia || null,
        notas: formData.notas || null,
        usuario_id: session.session.user.id,
      };

      let error;

      if (isEditing && editingMovimiento) {
        ({ error } = await supabase
          .from("inventario_movimientos")
          .update(movimientoData)
          .eq("id", editingMovimiento.id));
      } else {
        ({ error } = await supabase
          .from("inventario_movimientos")
          .insert([movimientoData]));
      }

      if (error) throw error;

      // Si es una entrada y el producto requiere fumigación, actualizar la fecha en el producto
      if (formData.tipo_movimiento === "entrada" && selectedProduct?.requiere_fumigacion && formData.fecha_ultima_fumigacion) {
        const { error: updateError } = await supabase
          .from("productos")
          .update({ fecha_ultima_fumigacion: formData.fecha_ultima_fumigacion })
          .eq("id", formData.producto_id);
        
        if (updateError) {
          console.error("Error actualizando fecha de fumigación:", updateError);
        }
      }

      toast({ title: isEditing ? "Movimiento actualizado" : "Movimiento registrado correctamente" });
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setSelectedProduct(null);
    setLotesProximosVencer([]);
    setIsEditing(false);
    setEditingMovimiento(null);
    setFormData({
      producto_id: "",
      tipo_movimiento: "entrada",
      cantidad: "",
      fecha_caducidad: "",
      fecha_ultima_fumigacion: "",
      lote: "",
      referencia: "",
      notas: "",
    });
  };

  const handleProductChange = async (productoId: string) => {
    const producto = productos.find((p) => p.id === productoId);
    setSelectedProduct(producto);
    setFormData({ ...formData, producto_id: productoId });
    
    // Buscar lotes próximos a vencer (30 días) o ya vencidos
    if (producto?.maneja_caducidad) {
      const hoy = new Date();
      const en30Dias = new Date();
      en30Dias.setDate(en30Dias.getDate() + 30);
      
      const { data: lotes } = await supabase
        .from("inventario_lotes")
        .select("*")
        .eq("producto_id", productoId)
        .gt("cantidad_disponible", 0)
        .lte("fecha_caducidad", en30Dias.toISOString().split('T')[0])
        .order("fecha_caducidad", { ascending: true });
      
      setLotesProximosVencer(lotes || []);
    } else {
      setLotesProximosVencer([]);
    }
  };

  const handleEditMovimiento = (movimiento: any) => {
    setIsEditing(true);
    setEditingMovimiento(movimiento);
    setSelectedProduct(productos.find((p) => p.id === movimiento.producto_id));
    setFormData({
      producto_id: movimiento.producto_id,
      tipo_movimiento: movimiento.tipo_movimiento,
      cantidad: String(movimiento.cantidad),
      fecha_caducidad: movimiento.fecha_caducidad || "",
      fecha_ultima_fumigacion: "",
      lote: movimiento.lote || "",
      referencia: movimiento.referencia || "",
      notas: movimiento.notas || "",
    });
    setDialogOpen(true);
  };

  const handleDeleteMovimiento = async (movimiento: any) => {
    const confirmar = window.confirm(
      "¿Seguro que quieres eliminar este movimiento de inventario? Esto afectará el stock."
    );
    if (!confirmar) return;

    try {
      const { error } = await supabase
        .from("inventario_movimientos")
        .delete()
        .eq("id", movimiento.id);

      if (error) throw error;

      toast({ title: "Movimiento eliminado" });
      loadData();
    } catch (error: any) {
      toast({
        title: "Error al eliminar",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Filtro para lotes (entradas)
  const filteredLotes = lotes.filter((lote) => {
    const matchesSearch = 
      lote.productos?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lote.productos?.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lote.lote_referencia?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesBodega = filterBodega === "todas" || lote.bodega_id === filterBodega;

    let matchesFecha = true;
    if (filterFechaInicio || filterFechaFin) {
      const loteFecha = new Date(lote.fecha_entrada).toISOString().split('T')[0];
      if (filterFechaInicio && loteFecha < filterFechaInicio) matchesFecha = false;
      if (filterFechaFin && loteFecha > filterFechaFin) matchesFecha = false;
    }

    return matchesSearch && matchesBodega && matchesFecha;
  });

  const filteredMovimientos = movimientos.filter((m) => {
    // Filtro por búsqueda de texto
    const matchesSearch = 
      m.productos?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.productos?.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.lote?.toLowerCase().includes(searchTerm.toLowerCase());

    // Filtro por tipo de movimiento
    const matchesTipo = filterTipo === "todos" || m.tipo_movimiento === filterTipo;

    // Filtro por rango de fechas
    let matchesFecha = true;
    if (filterFechaInicio || filterFechaFin) {
      const movimientoFecha = new Date(m.created_at).toISOString().split('T')[0];
      if (filterFechaInicio && movimientoFecha < filterFechaInicio) matchesFecha = false;
      if (filterFechaFin && movimientoFecha > filterFechaFin) matchesFecha = false;
    }

    return matchesSearch && matchesTipo && matchesFecha;
  });

  const getTipoMovimientoBadge = (tipo: string) => {
    const variants: Record<string, any> = {
      entrada: "default",
      salida: "destructive",
      ajuste: "secondary",
    };

    return (
      <Badge variant={variants[tipo] || "default"}>
        {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
      </Badge>
    );
  };

  const getStockChangeIndicator = (stockAnterior: number | null, stockNuevo: number | null, tipoMovimiento: string) => {
    if (stockAnterior === null || stockNuevo === null) return null;
    
    const diferencia = stockNuevo - stockAnterior;
    
    if (diferencia > 0) {
      return (
        <div className="flex items-center gap-2">
          <span className="font-semibold">{stockNuevo}</span>
          <div className="flex items-center gap-1 text-green-600">
            <ArrowUp className="h-4 w-4" />
            <span className="text-xs font-medium">+{diferencia}</span>
          </div>
        </div>
      );
    } else if (diferencia < 0) {
      return (
        <div className="flex items-center gap-2">
          <span className="font-semibold">{stockNuevo}</span>
          <div className="flex items-center gap-1 text-red-600">
            <ArrowDown className="h-4 w-4" />
            <span className="text-xs font-medium">{diferencia}</span>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2">
          <span className="font-semibold">{stockNuevo}</span>
          <div className="flex items-center gap-1 text-yellow-600">
            <Minus className="h-4 w-4" />
            <span className="text-xs font-medium">0</span>
          </div>
        </div>
      );
    }
  };

  const [activeTab, setActiveTab] = useState("productos");
  const [productosResumen, setProductosResumen] = useState<any[]>([]);
  const [searchProductos, setSearchProductos] = useState("");

  // Caducidad badge from shared utility

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        <NotificacionesSistema />
        
        <PageHeader
          eyebrow="Operaciones"
          title="Tu"
          titleAccent="inventario."
          lead="Control de stock y movimientos del almacén."
          actions={
            activeTab === "movimientos" ? (
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button onClick={resetForm}>
                    <Plus className="h-4 w-4 mr-2" />
                    {isEditing ? "Editar movimiento" : "Registrar Movimiento"}
                  </Button>
                </DialogTrigger>
                <DialogContent className={`${isMobile ? 'w-[calc(100vw-2rem)]' : 'max-w-2xl'} overflow-x-hidden`}>
                <DialogHeader>
                  <DialogTitle>
                    {isEditing ? "Editar Movimiento de Inventario" : "Registrar Movimiento de Inventario"}
                  </DialogTitle>
                  <DialogDescription>
                    Registra entradas, salidas o ajustes de inventario
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSave} className="space-y-4">
                  {lotesProximosVencer.length > 0 && (
                    <Alert variant="destructive" className="border-orange-500 bg-orange-50 text-orange-900">
                      <AlertTriangle className="h-4 w-4 !text-orange-600" />
                      <AlertTitle className="text-orange-800">
                        ¡Atención! Lotes próximos a vencer
                      </AlertTitle>
                      <AlertDescription className="text-orange-700">
                        <ul className="mt-2 space-y-1 text-sm">
                          {lotesProximosVencer.map((lote) => {
                            const fechaCaducidad = new Date(lote.fecha_caducidad);
                            const hoy = new Date();
                            const diasRestantes = Math.ceil((fechaCaducidad.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
                            const vencido = diasRestantes < 0;
                            
                            return (
                              <li key={lote.id} className="flex justify-between">
                                <span>
                                  Lote: {lote.lote_referencia || 'Sin ref.'} - {lote.cantidad_disponible} unidades
                                </span>
                                <span className={vencido ? "font-bold text-red-600" : "font-medium"}>
                                  {vencido 
                                    ? `¡VENCIDO hace ${Math.abs(diasRestantes)} días!`
                                    : `Vence en ${diasRestantes} días (${fechaCaducidad.toLocaleDateString('es-MX')})`
                                  }
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                    <div className="space-y-2">
                      <Label htmlFor="producto_id">Producto *</Label>
                      <Select
                        value={formData.producto_id}
                        onValueChange={handleProductChange}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona un producto" />
                        </SelectTrigger>
                        <SelectContent>
                          {productos.map((producto) => (
                            <SelectItem key={producto.id} value={producto.id}>
                              {producto.codigo} - {producto.nombre} (Stock: {producto.stock_actual})
                              {producto.maneja_caducidad && " 📅"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tipo_movimiento">Tipo de Movimiento *</Label>
                      <Select
                        value={formData.tipo_movimiento}
                        onValueChange={(value) => setFormData({ ...formData, tipo_movimiento: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="entrada">Entrada (compras/recepciones)</SelectItem>
                          <SelectItem value="salida">Salida (ventas)</SelectItem>
                          <SelectItem value="ajuste">Ajuste (correcciones)</SelectItem>
                          <SelectItem value="consumo_interno">Consumo interno (sin cobro)</SelectItem>
                          <SelectItem value="merma">Merma (producto dañado/perdido)</SelectItem>
                          <SelectItem value="transferencia">Transferencia (conversión entre productos)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                    <div className="space-y-2">
                      <Label htmlFor="cantidad">Cantidad *</Label>
                      <Input
                        id="cantidad"
                        type="number"
                        value={formData.cantidad}
                        onChange={(e) => setFormData({ ...formData, cantidad: e.target.value })}
                        required
                      />
                    </div>
                    {formData.tipo_movimiento === "entrada" && selectedProduct?.maneja_caducidad && (
                      <div className="space-y-2">
                        <Label htmlFor="fecha_caducidad">Fecha de Caducidad *</Label>
                        <Input
                          id="fecha_caducidad"
                          type="date"
                          value={formData.fecha_caducidad}
                          onChange={(e) => setFormData({ ...formData, fecha_caducidad: e.target.value })}
                          required
                        />
                        <p className="text-xs text-muted-foreground">
                          Este producto requiere fecha de caducidad para entradas
                        </p>
                      </div>
                    )}
                  </div>
                  {formData.tipo_movimiento === "entrada" && selectedProduct?.requiere_fumigacion && (
                    <div className="space-y-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <Label htmlFor="fecha_ultima_fumigacion">Fecha de última fumigación *</Label>
                      <Input
                        id="fecha_ultima_fumigacion"
                        type="date"
                        value={formData.fecha_ultima_fumigacion}
                        onChange={(e) => setFormData({ ...formData, fecha_ultima_fumigacion: e.target.value })}
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Este producto requiere registro de fumigación
                      </p>
                    </div>
                  )}
                  <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                    <div className="space-y-2">
                      <Label htmlFor="lote">Lote</Label>
                      <Input
                        id="lote"
                        value={formData.lote}
                        onChange={(e) => setFormData({ ...formData, lote: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="referencia">Referencia</Label>
                      <Input
                        id="referencia"
                        value={formData.referencia}
                        onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notas">Notas</Label>
                    <Input
                      id="notas"
                      value={formData.notas}
                      onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                    />
                  </div>
                  <div className={`flex ${isMobile ? 'flex-col' : 'justify-end'} gap-2`}>
                    {isMobile ? (
                      <>
                        <Button type="submit" className="w-full">{isEditing ? "Guardar cambios" : "Registrar"}</Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            setDialogOpen(false);
                            resetForm();
                          }}
                        >
                          Cancelar
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setDialogOpen(false);
                            resetForm();
                          }}
                        >
                          Cancelar
                        </Button>
                        <Button type="submit">{isEditing ? "Guardar cambios" : "Registrar"}</Button>
                      </>
                    )}
                  </div>
                </form>
              </DialogContent>
              </Dialog>
            ) : undefined
          }
        />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-transparent border-b border-ink-100 rounded-none p-0 h-auto gap-8 mb-6">
            <TabsTrigger value="productos" className="px-0 py-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-crimson-500 data-[state=active]:border-b-2 data-[state=active]:border-crimson-500 rounded-none text-ink-500 font-medium text-sm">
              Productos
            </TabsTrigger>
            <TabsTrigger value="lotes" className="px-0 py-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-crimson-500 data-[state=active]:border-b-2 data-[state=active]:border-crimson-500 rounded-none text-ink-500 font-medium text-sm">
              Lotes
            </TabsTrigger>
            <TabsTrigger value="movimientos" className="px-0 py-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-crimson-500 data-[state=active]:border-b-2 data-[state=active]:border-crimson-500 rounded-none text-ink-500 font-medium text-sm">
              Movimientos
            </TabsTrigger>
            <TabsTrigger value="categoria" className="px-0 py-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-crimson-500 data-[state=active]:border-b-2 data-[state=active]:border-crimson-500 rounded-none text-ink-500 font-medium text-sm">
              Por categoría
            </TabsTrigger>
          </TabsList>

          {/* ===== TAB PRODUCTOS ===== */}
          <TabsContent value="productos" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o código..."
                value={searchProductos}
                onChange={(e) => setSearchProductos(e.target.value)}
                className="pl-10"
              />
            </div>

            {(() => {
              const filtrados = productosResumen.filter(p =>
                p.nombre.toLowerCase().includes(searchProductos.toLowerCase()) ||
                p.codigo.toLowerCase().includes(searchProductos.toLowerCase())
              );
              const conStock = filtrados.filter(p => p.stockTotal > 0).length;
              const sinStock = filtrados.filter(p => p.stockTotal === 0).length;

              return (
                <>
                  <div className="flex items-center gap-6 mb-4 pb-4 border-b border-ink-100">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium mb-1">— Total</div>
                      <div className="font-sans text-2xl font-medium text-ink-900 tabular-nums">{filtrados.length}</div>
                    </div>
                    <div className="w-px h-10 bg-ink-100"></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium mb-1">— Con stock</div>
                      <div className="font-sans text-2xl font-medium tabular-nums" style={{color: 'hsl(var(--success))'}}>{conStock}</div>
                    </div>
                    <div className="w-px h-10 bg-ink-100"></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium mb-1">— Sin stock</div>
                      <div className="font-sans text-2xl font-medium text-destructive tabular-nums">{sinStock}</div>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-8">Cargando...</div>
                  ) : isMobile ? (
                    <div className="space-y-2">
                      {filtrados.map(p => (
                        <div key={p.id} className="p-3 border rounded-lg flex items-center justify-between">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="font-medium text-sm truncate">{p.nombre}</p>
                              {p.tieneVencimiento && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
                            </div>
                            <p className="text-xs text-muted-foreground">{p.codigo || "—"} · {p.categoria}</p>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className={`font-bold text-lg ${p.stockTotal === 0 ? "text-destructive" : p.stockTotal <= p.stockMinimo && p.stockMinimo > 0 ? "text-amber-600" : ""}`}>
                              {p.stockTotal.toLocaleString()}
                            </p>
                            <p className="text-xs text-muted-foreground">{p.unidad}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium">Código</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium">Producto</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium">Categoría</TableHead>
                            <TableHead className="text-right text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium">Stock Total</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium">Unidad</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium">Estado</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filtrados.map(p => (
                            <TableRow key={p.id} className={p.stockTotal === 0 ? "opacity-60" : ""}>
                              <TableCell className="font-mono text-xs">{p.codigo || "—"}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-medium">{p.nombre}</span>
                                  {p.tieneVencimiento && <span title="Lotes próximos a vencer"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /></span>}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">{p.categoria}</TableCell>
                              <TableCell className="text-right">
                                <span className={`font-bold ${p.stockTotal === 0 ? "text-destructive" : p.stockTotal <= p.stockMinimo && p.stockMinimo > 0 ? "text-amber-600" : ""}`}>
                                  {p.stockTotal.toLocaleString()}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">{p.unidad}</TableCell>
                              <TableCell>
                                {getStockStatusBadge(p.stockTotal, p.stockMinimo)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </>
              );
            })()}
          </TabsContent>

          <TabsContent value="lotes" className="space-y-4">
            {/* Filtros responsivos */}
            <div className={`flex ${isMobile ? 'flex-col' : 'flex-row flex-wrap'} gap-4`}>
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={isMobile ? "Buscar..." : "Buscar por producto, código o referencia de lote..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              {isMobile ? (
                <div className="grid grid-cols-2 gap-2">
                  <Select value={filterBodega} onValueChange={setFilterBodega}>
                    <SelectTrigger>
                      <SelectValue placeholder="Bodega" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todas">Todas</SelectItem>
                      {bodegas.map((bodega) => (
                        <SelectItem key={bodega.id} value={bodega.id}>
                          {bodega.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="date"
                    value={filterFechaInicio}
                    onChange={(e) => setFilterFechaInicio(e.target.value)}
                    placeholder="Desde"
                  />
                  <Input
                    type="date"
                    value={filterFechaFin}
                    onChange={(e) => setFilterFechaFin(e.target.value)}
                    placeholder="Hasta"
                  />
                  {(filterBodega !== "todas" || filterFechaInicio || filterFechaFin) && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFilterBodega("todas");
                        setFilterFechaInicio("");
                        setFilterFechaFin("");
                      }}
                    >
                      Limpiar
                    </Button>
                  )}
                </div>
              ) : (
                <Select value={filterBodega} onValueChange={setFilterBodega}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filtrar por bodega" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas las bodegas</SelectItem>
                    {bodegas.map((bodega) => (
                      <SelectItem key={bodega.id} value={bodega.id}>
                        {bodega.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {!isMobile && (
              <div className="flex gap-4 flex-wrap items-end">
                <div className="space-y-2">
                  <Label htmlFor="fecha_inicio_lotes">Desde</Label>
                  <Input
                    id="fecha_inicio_lotes"
                    type="date"
                    value={filterFechaInicio}
                    onChange={(e) => setFilterFechaInicio(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fecha_fin_lotes">Hasta</Label>
                  <Input
                    id="fecha_fin_lotes"
                    type="date"
                    value={filterFechaFin}
                    onChange={(e) => setFilterFechaFin(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                {(filterBodega !== "todas" || filterFechaInicio || filterFechaFin) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFilterBodega("todas");
                      setFilterFechaInicio("");
                      setFilterFechaFin("");
                    }}
                  >
                    Limpiar filtros
                  </Button>
                )}
                <div className="ml-auto text-sm text-muted-foreground">
                  Mostrando {filteredLotes.length} de {lotes.length} lotes
                </div>
              </div>
            )}

            {isMobile && (
              <div className="text-center text-sm text-muted-foreground">
                Mostrando {filteredLotes.length} de {lotes.length} lotes
              </div>
            )}

            {/* Contenido: Tarjetas móviles o Tabla desktop */}
            {loading ? (
              <div className="text-center py-8">Cargando...</div>
            ) : filteredLotes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No hay lotes registrados. Las entradas se crean desde Recepción en el módulo de Almacén.
              </div>
            ) : isMobile ? (
              <div className="space-y-3">
                {filteredLotes.map((lote) => {
                  const entregaId = lote.orden_compra_id ? entregasPorOrden[lote.orden_compra_id] : null;
                  return (
                    <LoteCardMobile
                      key={lote.id}
                      lote={lote}
                      entregaId={entregaId}
                      onVerRecepcion={(id) => {
                        setSelectedEntregaId(id);
                        setRecepcionDialogOpen(true);
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha Entrada</TableHead>
                      <TableHead>Producto</TableHead>
                      <TableHead>Cantidad Disponible</TableHead>
                      <TableHead>Bodega</TableHead>
                      <TableHead>Referencia Lote</TableHead>
                      <TableHead>Caducidad</TableHead>
                      <TableHead>Orden Compra</TableHead>
                      <TableHead>Recibido por</TableHead>
                      <TableHead className="w-[80px]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLotes.map((lote) => {
                      const entregaId = lote.orden_compra_id ? entregasPorOrden[lote.orden_compra_id] : null;
                      return (
                        <TableRow key={lote.id} className={lote.cantidad_disponible === 0 ? "opacity-50" : ""}>
                          <TableCell className="font-mono text-xs">
                            <div>{new Date(lote.fecha_entrada).toLocaleDateString('es-MX')}</div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{lote.productos?.codigo}</div>
                            <div className="text-sm text-muted-foreground">{lote.productos?.nombre}</div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold ${lote.cantidad_disponible === 0 ? 'text-muted-foreground' : ''}`}>
                                {lote.cantidad_disponible}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                {lote.productos?.unidad || 'uds'}
                              </span>
                              {lote.cantidad_disponible === 0 && (
                                <Badge variant="secondary">Agotado</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Warehouse className="h-3 w-3 text-muted-foreground" />
                              <span>{lote.bodegas?.nombre || "Sin asignar"}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-xs">{lote.lote_referencia || "—"}</span>
                          </TableCell>
                          <TableCell>
                            {lote.fecha_caducidad ? (
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3 text-muted-foreground" />
                                {getCaducidadBadge(lote.fecha_caducidad)}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {lote.ordenes_compra?.folio ? (
                              <Badge variant="outline">{lote.ordenes_compra.folio}</Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {lote.recibido_por?.full_name ? (
                              <span className="text-sm">{lote.recibido_por.full_name}</span>
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {entregaId ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedEntregaId(entregaId);
                                  setRecepcionDialogOpen(true);
                                }}
                                title="Ver detalle de recepción"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Diálogo de detalle de recepción */}
            <RecepcionDetalleDialog
              entregaId={selectedEntregaId}
              open={recepcionDialogOpen}
              onOpenChange={setRecepcionDialogOpen}
            />
          </TabsContent>

          {/* PESTAÑA MOVIMIENTOS MANUALES */}
          <TabsContent value="movimientos" className="space-y-4">
            {/* Filtros responsivos */}
            <div className={`flex ${isMobile ? 'flex-col' : 'flex-row flex-wrap'} gap-4`}>
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={isMobile ? "Buscar..." : "Buscar por producto, código o lote..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              {isMobile ? (
                <div className="grid grid-cols-2 gap-2">
                  <Select value={filterTipo} onValueChange={setFilterTipo}>
                    <SelectTrigger>
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="entrada">Entradas</SelectItem>
                      <SelectItem value="salida">Salidas</SelectItem>
                      <SelectItem value="ajuste">Ajustes</SelectItem>
                      <SelectItem value="merma">Merma</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="date"
                    value={filterFechaInicio}
                    onChange={(e) => setFilterFechaInicio(e.target.value)}
                    placeholder="Desde"
                  />
                  <Input
                    type="date"
                    value={filterFechaFin}
                    onChange={(e) => setFilterFechaFin(e.target.value)}
                    placeholder="Hasta"
                  />
                  {(filterTipo !== "todos" || filterFechaInicio || filterFechaFin) && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFilterTipo("todos");
                        setFilterFechaInicio("");
                        setFilterFechaFin("");
                      }}
                    >
                      Limpiar
                    </Button>
                  )}
                </div>
              ) : (
                <Select value={filterTipo} onValueChange={setFilterTipo}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Tipo de movimiento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los tipos</SelectItem>
                    <SelectItem value="entrada">Entradas</SelectItem>
                    <SelectItem value="salida">Salidas</SelectItem>
                    <SelectItem value="ajuste">Ajustes</SelectItem>
                    <SelectItem value="consumo_interno">Consumo interno</SelectItem>
                    <SelectItem value="merma">Merma</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {!isMobile && (
              <div className="flex gap-4 flex-wrap items-end">
                <div className="space-y-2">
                  <Label htmlFor="fecha_inicio">Desde</Label>
                  <Input
                    id="fecha_inicio"
                    type="date"
                    value={filterFechaInicio}
                    onChange={(e) => setFilterFechaInicio(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fecha_fin">Hasta</Label>
                  <Input
                    id="fecha_fin"
                    type="date"
                    value={filterFechaFin}
                    onChange={(e) => setFilterFechaFin(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                {(filterTipo !== "todos" || filterFechaInicio || filterFechaFin) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFilterTipo("todos");
                      setFilterFechaInicio("");
                      setFilterFechaFin("");
                    }}
                  >
                    Limpiar filtros
                  </Button>
                )}
                <div className="ml-auto text-sm text-muted-foreground">
                  Mostrando {filteredMovimientos.length} de {movimientos.length} movimientos
                </div>
              </div>
            )}

            {isMobile && (
              <div className="text-center text-sm text-muted-foreground">
                Mostrando {filteredMovimientos.length} de {movimientos.length} movimientos
              </div>
            )}

            {/* Contenido: Tarjetas móviles o Tabla desktop */}
            {loading ? (
              <div className="text-center py-8">Cargando...</div>
            ) : filteredMovimientos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No hay movimientos registrados
              </div>
            ) : isMobile ? (
              <div className="space-y-3">
                {filteredMovimientos.map((movimiento) => (
                  <MovimientoCardMobile
                    key={movimiento.id}
                    movimiento={movimiento}
                    onEdit={handleEditMovimiento}
                    onDelete={handleDeleteMovimiento}
                  />
                ))}
              </div>
            ) : (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Producto</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Cantidad</TableHead>
                      <TableHead>Stock Anterior</TableHead>
                      <TableHead>Stock Nuevo</TableHead>
                      <TableHead>Lote</TableHead>
                      <TableHead>Caducidad</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Referencia</TableHead>
                      <TableHead>Notas</TableHead>
                      <TableHead className="w-[140px] text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMovimientos.map((movimiento) => (
                      <TableRow key={movimiento.id}>
                        <TableCell className="font-mono text-xs">
                          <div>{new Date(movimiento.created_at).toLocaleDateString('es-MX')}</div>
                          <div className="text-muted-foreground">
                            {new Date(movimiento.created_at).toLocaleTimeString('es-MX')}
                          </div>
                        </TableCell>
                        <TableCell>
                          {movimiento.productos?.codigo} - {movimiento.productos?.nombre}
                        </TableCell>
                        <TableCell>{getTipoMovimientoBadge(movimiento.tipo_movimiento)}</TableCell>
                        <TableCell className="font-semibold">{movimiento.cantidad}</TableCell>
                        <TableCell className="text-muted-foreground">{movimiento.stock_anterior ?? "—"}</TableCell>
                        <TableCell>
                          {movimiento.stock_anterior !== null && movimiento.stock_nuevo !== null
                            ? getStockChangeIndicator(movimiento.stock_anterior, movimiento.stock_nuevo, movimiento.tipo_movimiento)
                            : movimiento.stock_nuevo ?? "—"}
                        </TableCell>
                        <TableCell>{movimiento.lote || "—"}</TableCell>
                        <TableCell>
                          {movimiento.fecha_caducidad
                            ? new Date(movimiento.fecha_caducidad).toLocaleDateString()
                            : "—"}
                        </TableCell>
                        <TableCell>{movimiento.profiles?.full_name || "—"}</TableCell>
                        <TableCell>{movimiento.referencia || "—"}</TableCell>
                        <TableCell className="max-w-[200px]">
                          {movimiento.notas ? (
                            <span className="text-sm text-muted-foreground truncate block" title={movimiento.notas}>{movimiento.notas}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditMovimiento(movimiento)}
                          >
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteMovimiento(movimiento)}
                          >
                            Eliminar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="categoria">
            <InventarioPorCategoria />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

// Componente principal con ErrorBoundary
const Inventario = () => (
  <ErrorBoundaryModule moduleName="Inventario">
    <InventarioContent />
  </ErrorBoundaryModule>
);

export default Inventario;